import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Spin, Empty, Input, Select, Tag, Image, Typography, Modal, Form, Upload, message } from 'antd';
import { SearchOutlined, PlusOutlined, UploadOutlined, ShoppingCartOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { getPuzzleProducts } from '../services/productApi';
import { useNotificationContext } from '../components/NotificationContext';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;

const PuzzleProducts = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const navigate = useNavigate();
  const notif = useNotificationContext();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await getPuzzleProducts();
      const data = response.data.ObjList || [];
      setProducts(data);
      setFilteredProducts(data);
    } catch (error) {
      console.error('Error fetching puzzle products:', error);
      message.error('مشکلی در دریافت محصولات پیش آمد');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    if (!value.trim()) {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product =>
        product.title.includes(value)
      );
      setFilteredProducts(filtered);
    }
  };

  const openOrderModal = (product) => {
    setSelectedProduct(product);
    setImageFile(null);
    setImagePreview(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleImageUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('لطفاً فقط فایل تصویری انتخاب کنید');
      return false;
    }
    
    const isLt5MB = file.size / 1024 / 1024 < 5;
    if (!isLt5MB) {
      message.error('حجم تصویر باید کمتر از 5MB باشد');
      return false;
    }
    
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
    return false;
  };

  const handleSubmitOrder = async (values) => {
    if (!imageFile) {
      message.warning('لطفاً تصویر مورد نظر برای چاپ روی پازل را انتخاب کنید');
      return;
    }

    setUploading(true);
    
    try {
      // Convert image to base64
      const reader = new FileReader();
      const imageBase64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(imageFile);
      });
      
      // Create designed order object
      const designedOrder = {
        Key: Date.now(),
        ProductName: selectedProduct.title,
        ProductSlug: selectedProduct.slug,
        ProductColor: null,
        ProductSize: null,
        ProductQuality: null,
        FirstDesignPic: imageBase64,
        SecondDesignPic: null,
        Amount: values.quantity || 1,
        UnitPrice: selectedProduct.price,
        AdditionalDesignImage: "[]",
        OrderType: "puzzle"
      };
      
      // Get existing orders from localStorage
      let existingOrders = JSON.parse(localStorage.getItem('designedOrders')) || [];
      existingOrders.push(designedOrder);
      localStorage.setItem('designedOrders', JSON.stringify(existingOrders));
      
      // Trigger cart update event for navbar badge
      window.dispatchEvent(new Event('cartUpdated'));
      
      notif.open({
        type: 'success',
        style: { width: "450px" },
        className: "fYekan-bold",
        placement: 'topRight',
        message: 'موفق',
        description: 'محصول به سبد خرید اضافه شد',
      });
      
      setModalVisible(false);
      
      // Ask user if they want to go to cart or continue shopping
      setTimeout(() => {
        Modal.confirm({
          title: 'محصول به سبد خرید اضافه شد',
          content: 'آیا می‌خواهید به سبد خرید بروید یا继续 خرید؟',
          okText: 'مشاهده سبد خرید',
          cancelText: 'ادامه خرید',
          onOk: () => navigate('/cart'),
          onCancel: () => setModalVisible(false),
        });
      }, 500);
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      message.error('خطا در افزودن به سبد خرید');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ direction: 'rtl', maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
      
      {/* Page Header */}
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <Title level={2} style={{ color: '#a23854', fontFamily: 'Yekan', marginBottom: 16 }}>
          پازل شخصی‌سازی شده
        </Title>
        <Paragraph style={{ maxWidth: 600, margin: '0 auto', color: '#666', fontFamily: 'Yekan' }}>
          تصویر دلخواه خود را انتخاب کنید تا روی پازل با کیفیت چاپ کنیم
        </Paragraph>
      </div>
      
      {/* Search Bar */}
      <div style={{ marginBottom: 32, maxWidth: 400, margin: '0 auto 32px' }}>
        <Search
          placeholder="جستجوی پازل..."
          allowClear
          enterButton={<SearchOutlined />}
          size="large"
          onSearch={handleSearch}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>
      
      {/* Products Grid */}
      {filteredProducts.length === 0 ? (
        <Empty description="محصولی یافت نشد" />
      ) : (
        <Row gutter={[24, 24]}>
          {filteredProducts.map(product => (
            <Col xs={24} sm={12} md={8} lg={6} key={product.id}>
              <Card
                hoverable
                className="light-glass-effect-card shaped-border"
                cover={
                  <div style={{ 
                    height: 250, 
                    overflow: 'hidden', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    background: '#f5f5f5',
                    padding: 20
                  }}>
                    {product.main_image ? (
                      <Image
                        src={product.main_image}
                        alt={product.title}
                        style={{ height: '100%', objectFit: 'contain' }}
                        preview={false}
                      />
                    ) : (
                      <div style={{ textAlign: 'center', color: '#999' }}>
                        <PlusOutlined style={{ fontSize: 48 }} />
                        <div>بدون تصویر</div>
                      </div>
                    )}
                  </div>
                }
                actions={[
                  <Button 
                    type="link" 
                    icon={<InfoCircleOutlined />}
                    onClick={() => window.open(`/product/${product.slug}`, '_blank')}
                  >
                    مشاهده جزئیات
                  </Button>,
                  <Button 
                    type="link" 
                    icon={<ShoppingCartOutlined />}
                    onClick={() => openOrderModal(product)}
                    style={{ color: '#a23854' }}
                  >
                    سفارش پازل
                  </Button>
                ]}
              >
                <div style={{ position: 'relative' }}>
                  {product.discount_percent > 0 && (
                    <Tag color="red" style={{ position: 'absolute', top: -30, right: 0 }}>
                      تخفیف {product.discount_percent}%
                    </Tag>
                  )}
                </div>
                <Card.Meta
                  title={<span style={{ fontFamily: 'Yekan', fontSize: '1rem', color: '#333' }}>{product.title}</span>}
                  description={
                    <div>
                      {product.discount_percent > 0 ? (
                        <>
                          <span style={{ color: '#a23854', fontWeight: 'bold', fontSize: '1.1rem' }}>
                            {product.price.toLocaleString()}
                          </span>
                          <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8, fontSize: '0.8rem' }}>
                            {product.base_price.toLocaleString()}
                          </span>
                        </>
                      ) : (
                        <span style={{ color: '#a23854', fontWeight: 'bold', fontSize: '1.1rem' }}>
                          {product.base_price.toLocaleString()}
                        </span>
                      )}
                      <span style={{ fontSize: '0.7rem', color: '#666' }}> تومان</span>
                      {product.short_description && (
                        <Paragraph 
                          ellipsis={{ rows: 2 }} 
                          style={{ marginTop: 8, fontSize: '0.7rem', color: '#666' }}
                        >
                          {product.short_description}
                        </Paragraph>
                      )}
                    </div>
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}
      
      {/* Order Modal */}
      <Modal
        title={`سفارش ${selectedProduct?.title}`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
        className="fYekan"
      >
        {selectedProduct && (
          <Form form={form} layout="vertical" onFinish={handleSubmitOrder}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              {selectedProduct.main_image ? (
                <img 
                  src={selectedProduct.main_image} 
                  alt={selectedProduct.title} 
                  style={{ height: 150, objectFit: 'contain', borderRadius: 8 }}
                />
              ) : (
                <div style={{ width: 150, height: 150, background: '#f0f0f0', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PlusOutlined style={{ fontSize: 32, color: '#ccc' }} />
                </div>
              )}
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ fontSize: 16, color: '#a23854' }}>{selectedProduct.title}</Text>
            </div>
            
            <div style={{ marginBottom: 16, padding: 12, background: '#f9f9f9', borderRadius: 8 }}>
              <Row>
                <Col span={12}>
                  <Text>قیمت پایه:</Text>
                </Col>
                <Col span={12} style={{ textAlign: 'left' }}>
                  <Text strong>{selectedProduct.base_price.toLocaleString()} تومان</Text>
                </Col>
              </Row>
              {selectedProduct.discount_percent > 0 && (
                <Row style={{ marginTop: 8 }}>
                  <Col span={12}>
                    <Text>تخفیف:</Text>
                  </Col>
                  <Col span={12} style={{ textAlign: 'left' }}>
                    <Text type="danger">{selectedProduct.discount_percent}%</Text>
                  </Col>
                </Row>
              )}
              <Row style={{ marginTop: 8 }}>
                <Col span={12}>
                  <Text strong>قیمت نهایی:</Text>
                </Col>
                <Col span={12} style={{ textAlign: 'left' }}>
                  <Text strong style={{ color: '#a23854', fontSize: 18 }}>
                    {selectedProduct.price.toLocaleString()} تومان
                  </Text>
                </Col>
              </Row>
            </div>
            
            <Form.Item name="quantity" label="تعداد" initialValue={1}>
              <Select style={{ width: 120 }}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                  <Option key={num} value={num}>{num}</Option>
                ))}
              </Select>
            </Form.Item>
            
            <Form.Item label="تصویر برای چاپ روی پازل" required>
              <div style={{ textAlign: 'center' }}>
                {imagePreview ? (
                  <div style={{ marginBottom: 16 }}>
                    <img 
                      src={imagePreview} 
                      alt="پیش‌نمایش" 
                      style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, border: '1px solid #ddd' }}
                    />
                    <Button 
                      type="link" 
                      danger 
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview(null);
                      }}
                      style={{ marginTop: 8 }}
                    >
                      حذف تصویر
                    </Button>
                  </div>
                ) : (
                  <Upload.Dragger
                    beforeUpload={handleImageUpload}
                    showUploadList={false}
                    accept="image/*"
                  >
                    <p className="ant-upload-drag-icon">
                      <UploadOutlined />
                    </p>
                    <p className="ant-upload-text">برای آپلود کلیک یا بکشید</p>
                    <p className="ant-upload-hint">
                      تصویر مورد نظر خود را برای چاپ روی پازل انتخاب کنید
                    </p>
                  </Upload.Dragger>
                )}
              </div>
            </Form.Item>
            
            <Form.Item>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Button onClick={() => setModalVisible(false)} style={{ width: '100%' }}>
                    انصراف
                  </Button>
                </Col>
                <Col span={12}>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={uploading}
                    style={{ width: '100%', background: '#a23854', borderColor: '#a23854' }}
                  >
                    افزودن به سبد خرید
                  </Button>
                </Col>
              </Row>
            </Form.Item>
          </Form>
        )}
      </Modal>
      
      <style>{`
        .ant-upload-drag {
          background: rgba(162,56,84,0.05) !important;
          border-color: #a23854 !important;
        }
        .ant-upload-drag:hover {
          border-color: #a23854 !important;
        }
      `}</style>
    </div>
  );
};

export default PuzzleProducts;